/* eslint-disable @typescript-eslint/no-explicit-any */
import JanusMessage from 'api/janus/client/misc/message'

import Plugin from '../../client/plugin'

interface CreatePeerConnectionOptions {
  rtcConfiguration?: RTCConfiguration
  createDataChannel?: boolean
}

class MediaPlugin extends Plugin {
  private readonly pcListeners: Record<string, (ev: Event) => void> = {}
  private pc: RTCPeerConnection | null = null
  protected dataChannel: RTCDataChannel

  createPeerConnection(config: CreatePeerConnectionOptions = {}): RTCPeerConnection {
    const pcConfig = Object.assign(
      {},
      this.getSession().getConnection().getOptions().pc,
      config.rtcConfiguration,
    )

    this.pc = new RTCPeerConnection(pcConfig)

    this.addPcEventListeners()

    if (config.createDataChannel) {
      this.createDataChannel()
    }

    return this.pc
  }

  createDataChannel() {
    this.dataChannel = this.pc.createDataChannel('JanusDataChannel')
    this.dataChannel.addEventListener('open', (ev) => this.onDataChannelOpen(ev))
    this.dataChannel.addEventListener('close', (ev) => this.onDataChannelClose(ev))
    this.dataChannel.addEventListener('message', (ev) => this.onDataChannelMessage(ev))
  }

  onDataChannelMessage(ev: MessageEvent<string>) {
    this.emit('pc:datachannel:message', ev.data)
  }

  onDataChannelClose(ev: Event) {
    this.emit('pc:datachannel:close', ev.target as RTCDataChannel)
  }

  onDataChannelOpen(ev: Event) {
    this.emit('pc:datachannel:open', ev.target as RTCDataChannel)
  }

  getPeerConnection(): RTCPeerConnection | null {
    return this.pc
  }

  hangup(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.once('hangup', resolve)
      this.sendWithTransaction({ janus: 'hangup' }).catch(reject)
    })
  }

  addTrack(track: MediaStreamTrack, stream: MediaStream) {
    this.pc?.addTrack(track, stream)
    this.emit('pc:track:local', { track, streams: [stream] })
  }

  createOffer(options: RTCOfferOptions): Promise<any> {
    return this.createSDP('createOffer', options)
  }

  createAnswer(jsep: RTCSessionDescription, options: RTCAnswerOptions): Promise<any> {
    return this.setRemoteSDP(jsep).then(() => this.createSDP('createAnswer', options))
  }

  setRemoteSDP(jsep: RTCSessionDescription): Promise<any> {
    return Promise.resolve(this.pc?.setRemoteDescription(new RTCSessionDescription(jsep)))
  }

  private createSDP(party: string, options: RTCAnswerOptions | RTCOfferOptions): Promise<any> {
    if (!this.pc) {
      throw new Error('Create PeerConnection before creating SDP for it.')
    }

    if (['createOffer', 'createAnswer'].indexOf(party) < 0) {
      throw new Error('Unknown party in createSDP.')
    }

    options = options ?? {}

    return this.pc[party](options)
      .then((description) => this.pc?.setLocalDescription(description))
      .then(() => this.pc?.localDescription)
  }

  processIncomeMessage(message: JanusMessage) {
    return super.processIncomeMessage(message).then((result) => {
      const janusType = message.get('janus')
      switch (janusType) {
        case 'trickle':
          this.onTrickle(message)
          break
        case 'hangup':
          this.onHangup(message)
          break

        case 'slowlink':
          this.onSlowlink()
          break
      }
      return result
    })
  }

  closePeerConnection() {
    if (this.pc) {
      this.stopLocalMedia()
      Object.keys(this.pcListeners).forEach((event) => this.removePcEventListener(event))
      this.pc.close()
      this.pc = null
      this.emit('pc:close')
    }
  }

  onDetached() {
    this.closePeerConnection()
    return super.onDetached()
  }

  onHangup(msg: any) {
    this.emit('hangup', msg)
  }

  onSlowlink() {
    this.emit('slowlink')
  }

  private onTrickle(msg: any) {
    const candidate = new RTCIceCandidate(msg.candidate)
    this.pc?.addIceCandidate(candidate).catch((error) => this.emit('pc:error', error))
  }

  private stopLocalMedia() {
    this.pc?.getSenders().forEach((sender: RTCRtpSender) => sender.track?.stop())
  }

  private addPcEventListeners() {
    this.addPcEventListener('connectionstatechange', () => {
      if (this.pc.connectionState === 'connected') this.emit('pc:connected')
    })

    this.addPcEventListener('datachannel', (ev) => {
      this.dataChannel = ev.channel
      this.dataChannel.addEventListener('open', (ev) => this.onDataChannelOpen(ev))
      this.dataChannel.addEventListener('close', (ev) => this.onDataChannelClose(ev))
      this.dataChannel.addEventListener('message', (ev) => this.onDataChannelMessage(ev))
    })

    this.addPcEventListener('track', (event) => {
      this.emit('pc:track:remote', event)
    })

    this.addPcEventListener('icecandidate', (event) => {
      if (event.candidate) {
        this.send({ janus: 'trickle', candidate: event.candidate })
      } else {
        this.send({ janus: 'trickle', candidate: { completed: true } })
        this.removePcEventListener('icecandidate')
      }
    })

    this.addPcEventListener('signalingstatechange', () => {
      if (this.pc?.signalingState === 'closed') {
        this.closePeerConnection()
      }
    })

    this.addPcEventListener('iceconnectionstatechange', () => {
      switch (this.pc?.iceConnectionState) {
        case 'closed':
        case 'failed':
          this.closePeerConnection()
          break
      }
    })
  }

  private addPcEventListener<E extends keyof RTCPeerConnectionEventMap>(
    event: E,
    listener: (ev: RTCPeerConnectionEventMap[E]) => void,
  ) {
    this.pcListeners[event] = listener
    this.pc?.addEventListener(event, listener)
  }

  private removePcEventListener(event) {
    this.pc?.removeEventListener(event, this.pcListeners[event])
    delete this.pcListeners[event]
  }
}

export interface UserMediaResult {
  stream?: MediaStream
  error?: any
}

export default MediaPlugin
